/**
 * Signed Academy figure URLs (SIR-HYG-03).
 *
 * The backend no longer serves `/academy/assets/*` to anyone who guesses a key. Question
 * responses now carry `asset_urls: Record<imageKey, signedPath>` (3-hour signatures, longer than an
 * exam sitting). If a figure still fails to load (an expired or rejected signature), the image
 * component refetches the query whose data carried that key once (`refetchAcademyAssetOwners`),
 * which remembers fresh signed paths.
 * `api()` passes every JSON response to `rememberAcademyAssetUrls`, and figure components ask
 * `academyAssetPath(key)` for the path, then prefix `BASE_URL`. Components never compose
 * asset URLs themselves.
 *
 * If a key has no signed path (an old backend during rollout), the unsigned path is returned;
 * the backend serves it only while `ACADEMY_ASSETS_SIGNATURE_MODE=report`.
 */

const signedPaths = new Map<string, string>();
const ASSET_ROUTE = '/academy/assets/';
/** Matches the backend walk limit; question pages are far smaller. */
const MAX_VISITED_NODES = 50_000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** Walks a JSON payload and calls `visit` for every `asset_urls` record (bounded walk). */
function eachAssetUrls(payload: unknown, visit: (urls: Record<string, unknown>) => boolean): void {
  const stack: unknown[] = [payload];
  let visited = 0;
  while (stack.length > 0 && visited < MAX_VISITED_NODES) {
    const node = stack.pop();
    visited += 1;
    if (Array.isArray(node)) {
      for (const item of node) stack.push(item);
      continue;
    }
    if (!isRecord(node)) continue;
    const urls = node.asset_urls;
    if (isRecord(urls) && visit(urls)) return;
    for (const [field, child] of Object.entries(node)) {
      if (field !== 'asset_urls' && child && typeof child === 'object') stack.push(child);
    }
  }
}

export function rememberAcademyAssetUrls(payload: unknown): void {
  eachAssetUrls(payload, (urls) => {
    for (const [key, path] of Object.entries(urls)) {
      if (typeof path === 'string' && path.startsWith(ASSET_ROUTE)) signedPaths.set(key, path);
    }
    return false;
  });
}

/** True when this response carried a signed URL for `key` (it is the query that owns the figure). */
export function payloadOwnsAcademyAsset(payload: unknown, key: string): boolean {
  let owns = false;
  eachAssetUrls(payload, (urls) => {
    owns = typeof urls[key] === 'string';
    return owns;
  });
  return owns;
}

/** The part of a TanStack `QueryClient` this module needs (kept narrow for tests). */
export interface AcademyAssetQueryClient {
  refetchQueries(filters: {
    type: 'active';
    predicate: (query: { state: { data: unknown } }) => boolean;
  }): Promise<unknown>;
}

const inFlight = new Map<string, Promise<void>>();

/**
 * Refetches, once, the active queries whose data carried a signed URL for `key`. Their responses
 * pass through `api()`, which remembers the fresh signed paths. Several broken images of one
 * question share one refetch. Never throws: a failed refetch leaves the old image in place.
 */
export function refetchAcademyAssetOwners(
  client: AcademyAssetQueryClient,
  key: string,
): Promise<void> {
  const pending = inFlight.get(key);
  if (pending) return pending;
  const run = client
    .refetchQueries({
      type: 'active',
      predicate: (query) => payloadOwnsAcademyAsset(query.state.data, key),
    })
    .then(
      () => undefined,
      () => undefined,
    )
    .finally(() => inFlight.delete(key));
  inFlight.set(key, run);
  return run;
}

function unsignedPath(key: string): string {
  return `${ASSET_ROUTE}${key.split('/').map(encodeURIComponent).join('/')}`;
}

/** Path (no host) for an Academy image key; prefix it with the API base URL. */
export function academyAssetPath(key: string): string {
  return signedPaths.get(key) ?? unsignedPath(key);
}

/** Test helper: forget every remembered signed path. */
export function resetAcademyAssetUrls(): void {
  signedPaths.clear();
  inFlight.clear();
}
